import React, { useState, useEffect } from 'react';
import {
    Box,
    Heading,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Spinner,
    Alert,
    AlertIcon,
    Select,
    HStack,
    VStack,
    Text,
    Badge,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    SimpleGrid,
    Card,
    CardBody,
    CardHeader,
    useToast,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    useDisclosure,
    Flex,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Stack,
    Divider,
    useBreakpointValue,
} from '@chakra-ui/react';
import { FiMoreVertical, FiCheck, FiClock, FiX, FiTrash2, FiLock, FiEye, FiRefreshCw } from 'react-icons/fi';
import { apiService } from '../services/apiService';
import SumUpPaymentButton from '../components/Payment/SumUpPaymentButton';
import MemberSelect from '../components/MemberSelect';

const LIBELLES_MODE_PAIEMENT = {
    especes: 'Espèces', cheque: 'Chèque', virement: 'Virement bancaire',
    carte_bancaire: 'Carte bancaire', paypal: 'PayPal', sumup: 'SumUp',
};

export default function Contributions() {
    const [contributions, setContributions] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [filterType, setFilterType] = useState('');
    const [filterMember, setFilterMember] = useState('');
    const [filterStatut, setFilterStatut] = useState('');
    const [currentUser, setCurrentUser] = useState(null);
    const [members, setMembers] = useState([]);
    const [contributionTypes, setContributionTypes] = useState([]);
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    // Modal de rejet (avec motif obligatoire)
    const { isOpen: isRejectOpen, onOpen: onRejectOpen, onClose: onRejectClose } = useDisclosure();
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    // Modal de détails d'une cotisation
    const { isOpen: isDetailsOpen, onOpen: onDetailsOpen, onClose: onDetailsClose } = useDisclosure();
    const [detailsContrib, setDetailsContrib] = useState(null);

    // État du formulaire d'ajout
    const [formData, setFormData] = useState({
        user_id: '',
        type_id: '',
        montant: '',
        date_contribution: new Date().toISOString().split('T')[0],
        mode_paiement: 'especes',
        reference_paiement: '',
        statut: 'paye'
    });

    const isMobile = useBreakpointValue({ base: true, md: false });

    const months = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];

    const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

    useEffect(() => {
        fetchCurrentUser();
    }, []);

    const fetchMembers = async() => {
        try {
            const data = await apiService.getMembers({ limit: 1000 });
            setMembers(data.members || []);
        } catch (err) {
            console.error('Error fetching members:', err);
        }
    };

    const fetchContributionTypes = async() => {
        try {
            const data = await apiService.makeRequest('/contributions/types/list');
            setContributionTypes(data || []);
        } catch (err) {
            console.error('Error fetching contribution types:', err);
        }
    };

    useEffect(() => {
        if (currentUser) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, selectedYear]);

    const fetchCurrentUser = async() => {
        try {
            const user = await apiService.getCurrentUser();
            setCurrentUser(user);

            // Si admin, pasteur, trésorier ou secrétaire, charger les données nécessaires
            const canManage = user.is_admin || ['admin', 'pasteur', 'president', 'tresorier', 'secretaire', 'commissaire'].includes(user.role);
            
            if (canManage) {
                fetchMembers();
                fetchContributionTypes();
            }
        } catch (err) {
            console.error('Error fetching user:', err);
        }
    };

    const handleSubmit = async() => {
        try {
            await apiService.makeRequest('/contributions', {
                method: 'POST',
                body: JSON.stringify(estSecretaire ? { ...formData, statut: 'en_attente' } : formData)
            });

            toast({
                title: 'Contribution ajoutée',
                description: 'La contribution a été enregistrée avec succès',
                status: 'success',
                duration: 3000,
            });

            onClose();
            fetchData();

            // Réinitialiser le formulaire
            setFormData({
                user_id: '',
                type_id: '',
                montant: '',
                date_contribution: new Date().toISOString().split('T')[0],
                mode_paiement: 'especes',
                reference_paiement: '',
                statut: 'paye'
            });
        } catch (err) {
            toast({
                title: 'Erreur',
                description: err.message || 'Erreur lors de l\'ajout de la contribution',
                status: 'error',
                duration: 5000,
            });
        }
    };

    const updateContributionStatus = async(contributionId, newStatus, notes) => {
        try {
            const body = { statut: newStatus };
            if (notes !== undefined) body.notes = notes;

            await apiService.makeRequest(`/contributions/${contributionId}/status`, {
                method: 'PUT',
                body: JSON.stringify(body)
            });

            toast({
                title: 'Statut mis à jour',
                description: `La contribution est maintenant "${getStatusLabel(newStatus)}"`,
                status: 'success',
                duration: 3000,
            });

            fetchData();
        } catch (err) {
            toast({
                title: 'Erreur',
                description: err.message || 'Erreur lors de la mise à jour du statut',
                status: 'error',
                duration: 5000,
            });
        }
    };

    const openRejectModal = (contrib) => {
        setRejectTarget(contrib);
        setRejectReason('');
        onRejectOpen();
    };

    const confirmReject = async () => {
        if (!rejectReason.trim()) {
            toast({
                title: 'Motif requis',
                description: 'Veuillez indiquer la raison du rejet',
                status: 'warning',
                duration: 3000,
            });
            return;
        }
        await updateContributionStatus(rejectTarget.id, 'refuse', rejectReason.trim());
        onRejectClose();
    };

    const openDetailsModal = (contrib) => {
        setDetailsContrib(contrib);
        onDetailsOpen();
    };

    const [reverifyingId, setReverifyingId] = useState(null);
    const reverifySumup = async (contrib) => {
        setReverifyingId(contrib.id);
        try {
            const data = await apiService.makeRequest(`/payments/sumup/reverify-contribution/${contrib.id}`, {
                method: 'POST'
            });
            if (data.is_paid) {
                toast({ title: 'Paiement confirmé', description: 'La cotisation a été marquée comme payée', status: 'success', duration: 4000 });
            } else {
                toast({ title: 'Toujours en attente', description: `Statut SumUp : ${data.status || 'inconnu'}`, status: 'info', duration: 4000 });
            }
            fetchData();
        } catch (err) {
            toast({ title: 'Erreur', description: err.message || 'Erreur lors de la vérification', status: 'error', duration: 5000 });
        } finally {
            setReverifyingId(null);
        }
    };

    const deleteContribution = async(contributionId) => {
        if (!window.confirm('Supprimer définitivement cette contribution ?')) return;
        try {
            await apiService.makeRequest(`/contributions/${contributionId}`, {
                method: 'DELETE'
            });

            toast({
                title: 'Contribution supprimée',
                status: 'success',
                duration: 3000,
            });

            fetchData();
        } catch (err) {
            toast({
                title: 'Erreur',
                description: err.message || 'Erreur lors de la suppression',
                status: 'error',
                duration: 5000,
            });
        }
    };

    // Un paiement en ligne (SumUp, carte, PayPal) est vérifié automatiquement
    // auprès du prestataire — il ne doit jamais être modifiable/révocable
    // manuellement, contrairement à une saisie manuelle (espèces, chèque, virement).
    const MODES_PAIEMENT_EN_LIGNE = ['sumup', 'carte_bancaire', 'paypal'];
    const estPaiementEnLigne = (contrib) => MODES_PAIEMENT_EN_LIGNE.includes(contrib.mode_paiement);

    const fetchData = async() => {
        try {
            setLoading(true);

            // Récupérer les contributions
            const canManage = currentUser.is_admin ||
                             ['admin', 'pasteur', 'president', 'tresorier', 'secretaire', 'commissaire'].includes(currentUser.role);
            
            const params = canManage ? { year: selectedYear } : { user_id: currentUser.id, year: selectedYear };

            const contribData = await apiService.getContributions(params);
            setContributions(contribData.contributions || []);

            // Récupérer le résumé si membre (pas admin/pasteur/trésorier)
            if (!canManage) {
                const summaryData = await apiService.makeRequest(
                    `/members/${currentUser.id}/contributions-summary`
                );
                setSummary(summaryData);
            }

        } catch (err) {
            console.error('Error fetching data:', err);
            toast({
                title: 'Erreur',
                description: err.message || 'Erreur lors du chargement des données',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // Contributions filtrées (côté client)
    const filteredContributions = contributions.filter(c => {
        if (filterType   && String(c.type_id) !== String(filterType))   return false;
        if (filterMember && String(c.user_id) !== String(filterMember)) return false;
        if (filterStatut && c.statut !== filterStatut)                  return false;
        return true;
    });

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'paye':
                return 'green';
            case 'en_attente':
                return 'orange';
            case 'refuse':
            case 'rejete':
                return 'red';
            case 'rembourse':
                return 'purple';
            case 'echoue':
                return 'gray';
            default:
                return 'gray';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'paye':
                return 'Payé';
            case 'en_attente':
                return 'En attente';
            case 'refuse':
            case 'rejete':
                return 'Rejeté';
            case 'rembourse':
                return 'Remboursé';
            case 'echoue':
                return 'Échoué';
            default:
                return status;
        }
    };

    const sendBulkReceipts = async() => {
        const year = prompt('Année fiscale ?', new Date().getFullYear());
        if (!year) return;

        try {
            const response = await apiService.makeRequest('/contributions/send-receipts-bulk', {
                method: 'POST',
                body: JSON.stringify({ year, method: 'email' })
            });

            toast({
                title: 'Succès',
                description: response.message,
                status: 'success',
                duration: 5000,
            });
        } catch (error) {
            toast({
                title: 'Erreur',
                description: error.message,
                status: 'error',
                duration: 5000,
            });
        }
    };

    // Grouper les contributions par mois
    const contributionsByMonth = contributions.reduce((acc, contrib) => {
        const date = new Date(contrib.date_contribution);
        const month = date.getMonth();
        if (!acc[month]) {
            acc[month] = [];
        }
        acc[month].push(contrib);
        return acc;
    }, {});

    // Calculer les statistiques pour l'admin/pasteur/trésorier ou le membre
    const calculateStats = () => {
        const canManage = currentUser?.is_admin || ['admin', 'pasteur', 'president', 'tresorier', 'secretaire', 'commissaire'].includes(currentUser?.role);
        
        if (canManage) {
            return contributions.reduce((acc, contrib) => {
                acc.total++;
                if (contrib.statut === 'paye') {
                    acc.paye++;
                    acc.montant_total += parseFloat(contrib.montant);
                } else if (contrib.statut === 'en_attente') {
                    acc.en_attente++;
                }
                return acc;
            }, { total: 0, paye: 0, en_attente: 0, montant_total: 0 });
        }
        return null;
    };

    const stats = calculateStats();

    if (loading && !summary) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
                <Spinner size="xl" color="blue.500" />
            </Box>
        );
    }

    // Vue Admin, Pasteur ou Trésorier
    const canManageContributions = currentUser?.is_admin || ['admin', 'pasteur', 'president', 'tresorier', 'secretaire', 'commissaire'].includes(currentUser?.role);
    const canValiderContributions = currentUser?.is_admin || ['admin', 'pasteur', 'president', 'tresorier'].includes(currentUser?.role);
    const estSecretaire = currentUser?.role === 'secretaire' && !currentUser?.is_admin;
    const estCommissaire = currentUser?.role === 'commissaire' && !currentUser?.is_admin; // lecture seule

    const renderActions = (contrib) => (
        <HStack spacing={1}>
            <IconButton
                icon={<FiEye />}
                variant="ghost"
                size="sm"
                onClick={() => openDetailsModal(contrib)}
                title="Voir les détails"
                aria-label="Voir les détails"
            />
            {contrib.mode_paiement === 'sumup' && contrib.statut === 'en_attente' && !estCommissaire && (
                <IconButton
                    icon={<FiRefreshCw />}
                    variant="ghost"
                    size="sm"
                    colorScheme="blue"
                    isLoading={reverifyingId === contrib.id}
                    onClick={() => reverifySumup(contrib)}
                    title="Revérifier ce paiement auprès de SumUp"
                    aria-label="Revérifier le paiement SumUp"
                />
            )}
            {canValiderContributions && (
                estPaiementEnLigne(contrib) && contrib.statut === 'paye' ? (
                    <IconButton
                        icon={<FiLock />}
                        variant="ghost"
                        size="sm"
                        isDisabled
                        title="Paiement en ligne vérifié automatiquement — non modifiable"
                        aria-label="Paiement en ligne non modifiable"
                    />
                ) : (
                    <Menu>
                        <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            variant="ghost"
                            size="sm"
                            aria-label="Actions"
                        />
                        <MenuList>
                            <MenuItem
                                icon={<FiCheck />}
                                onClick={() => updateContributionStatus(contrib.id, 'paye')}
                                isDisabled={contrib.statut === 'paye'}
                            >
                                Marquer comme payé
                            </MenuItem>
                            <MenuItem
                                icon={<FiClock />}
                                onClick={() => updateContributionStatus(contrib.id, 'en_attente')}
                                isDisabled={contrib.statut === 'en_attente'}
                            >
                                Mettre en attente
                            </MenuItem>
                            <MenuItem
                                icon={<FiX />}
                                onClick={() => openRejectModal(contrib)}
                                isDisabled={contrib.statut === 'refuse' || contrib.statut === 'rejete'}
                                color="red.500"
                            >
                                Rejeter
                            </MenuItem>
                            <MenuItem
                                icon={<FiTrash2 />}
                                onClick={() => deleteContribution(contrib.id)}
                                color="red.600"
                            >
                                Supprimer
                            </MenuItem>
                        </MenuList>
                    </Menu>
                )
            )}
        </HStack>
    );

    if (canManageContributions) {
        return (
            <Box>
                <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
                    <Heading size="lg">Contributions des Membres</Heading>
                    <HStack flexWrap="wrap">
                        {currentUser?.is_admin && (
                            <Button colorScheme="blue" onClick={sendBulkReceipts}>
                                Envoyer les reçus fiscaux en masse
                            </Button>
                        )}
                        {!estCommissaire && (
                            <Button colorScheme="green" onClick={onOpen}>
                                + Ajouter une contribution
                            </Button>
                        )}
                    </HStack>
                </Flex>

                {/* Statistiques Admin */}
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
                    <Stat bg="white" p={4} borderRadius="md" shadow="sm">
                        <StatLabel>Total contributions</StatLabel>
                        <StatNumber>{stats.total}</StatNumber>
                    </Stat>
                    <Stat bg="white" p={4} borderRadius="md" shadow="sm">
                        <StatLabel>Payées</StatLabel>
                        <StatNumber color="green.500">{stats.paye}</StatNumber>
                    </Stat>
                    <Stat bg="white" p={4} borderRadius="md" shadow="sm">
                        <StatLabel>En attente</StatLabel>
                        <StatNumber color="orange.500">{stats.en_attente}</StatNumber>
                    </Stat>
                    <Stat bg="white" p={4} borderRadius="md" shadow="sm">
                        <StatLabel>Montant total</StatLabel>
                        <StatNumber color="blue.600">{formatAmount(stats.montant_total)}</StatNumber>
                    </Stat>
                </SimpleGrid>

                {/* Filtres */}
                <Card mb={6}>
                    <CardBody>
                        <HStack flexWrap="wrap" spacing={3}>
                            <HStack>
                                <Text fontWeight="medium" whiteSpace="nowrap">Année :</Text>
                                <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} w="120px">
                                    {years.map(year => <option key={year} value={year}>{year}</option>)}
                                </Select>
                            </HStack>
                            {canManageContributions && (
                                <>
                                    <HStack>
                                        <Text fontWeight="medium" whiteSpace="nowrap">Type :</Text>
                                        <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} w="200px" placeholder="Tous les types">
                                            {contributionTypes.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
                                        </Select>
                                    </HStack>
                                    <HStack>
                                        <Text fontWeight="medium" whiteSpace="nowrap">Membre :</Text>
                                        <Box w="250px">
                                            <MemberSelect
                                                members={[...members].sort((a, b) => a.nom.localeCompare(b.nom))}
                                                value={filterMember}
                                                onChange={(id) => setFilterMember(id ? String(id) : '')}
                                                placeholder="Rechercher un membre..."
                                            />
                                        </Box>
                                        {filterMember && (
                                            <Button size="xs" variant="ghost" colorScheme="gray" onClick={() => setFilterMember('')}>✕</Button>
                                        )}
                                    </HStack>
                                    <HStack>
                                        <Text fontWeight="medium" whiteSpace="nowrap">Statut :</Text>
                                        <Select value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)} w="150px" placeholder="Tous">
                                            <option value="paye">Payé</option>
                                            <option value="en_attente">En attente</option>
                                            <option value="refuse">Refusé</option>
                                            <option value="rembourse">Remboursé</option>
                                            <option value="echoue">Échoué</option>
                                        </Select>
                                    </HStack>
                                    {(filterType || filterMember || filterStatut) && (
                                        <Button size="sm" variant="ghost" colorScheme="gray"
                                            onClick={() => { setFilterType(''); setFilterMember(''); setFilterStatut(''); }}>
                                            ✕ Réinitialiser
                                        </Button>
                                    )}
                                </>
                            )}
                        </HStack>
                        {(filterType || filterMember || filterStatut) && (
                            <Text fontSize="xs" color="blue.600" mt={2}>
                                {filteredContributions.length} résultat{filteredContributions.length > 1 ? 's' : ''} sur {contributions.length} cotisations
                            </Text>
                        )}
                    </CardBody>
                </Card>

                {/* Tableau des contributions */}
                <Card>
                    <CardBody p={0}>
                        {isMobile ? (
                            <Stack spacing={3} p={3} divider={<Divider />}>
                                {filteredContributions.length === 0 ? (
                                    <Text color="gray.500" textAlign="center" py={6}>Aucune contribution trouvée</Text>
                                ) : (
                                    filteredContributions.map((contrib) => (
                                        <Box key={contrib.id}>
                                            <HStack justify="space-between" align="flex-start" mb={1}>
                                                <Text fontWeight="semibold">{contrib.membre_nom} {contrib.membre_prenom}</Text>
                                                <Badge colorScheme={getStatusColor(contrib.statut)}>
                                                    {getStatusLabel(contrib.statut)}
                                                </Badge>
                                            </HStack>
                                            <HStack justify="space-between" mb={1}>
                                                <Text fontSize="sm" color="gray.600">{contrib.type_nom}</Text>
                                                <Text fontWeight="bold">{formatAmount(contrib.montant)}</Text>
                                            </HStack>
                                            <HStack justify="space-between">
                                                <Text fontSize="sm" color="gray.500">
                                                    {formatDate(contrib.date_contribution)}
                                                    {contrib.reference_paiement ? ' · ' + contrib.reference_paiement : ''}
                                                </Text>
                                                {renderActions(contrib)}
                                            </HStack>
                                        </Box>
                                    ))
                                )}
                            </Stack>
                        ) : (
                            <TableContainer maxH="65vh" overflowY="auto">
                                <Table variant="simple">
                                    <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                                        <Tr>
                                            <Th>Date</Th>
                                            <Th>Membre</Th>
                                            <Th>Type</Th>
                                            <Th isNumeric>Montant</Th>
                                            <Th>Statut</Th>
                                            <Th>Actions</Th>
                                            <Th>Référence</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {filteredContributions.length === 0 ? (
                                            <Tr>
                                                <Td colSpan={7} textAlign="center" py={10}>
                                                    <Text color="gray.500">Aucune contribution trouvée</Text>
                                                </Td>
                                            </Tr>
                                        ) : (
                                            filteredContributions.map((contrib) => (
                                                <Tr key={contrib.id} _hover={{ bg: 'gray.50' }}>
                                                    <Td>{formatDate(contrib.date_contribution)}</Td>
                                                    <Td fontWeight="medium">
                                                        {contrib.membre_nom} {contrib.membre_prenom}
                                                    </Td>
                                                    <Td>{contrib.type_nom}</Td>
                                                    <Td isNumeric fontWeight="semibold">
                                                        {formatAmount(contrib.montant)}
                                                    </Td>
                                                    <Td>
                                                        <Badge colorScheme={getStatusColor(contrib.statut)}>
                                                            {getStatusLabel(contrib.statut)}
                                                        </Badge>
                                                    </Td>
                                                    <Td>{renderActions(contrib)}</Td>
                                                    <Td fontSize="sm" color="gray.600">
                                                        {contrib.reference_paiement || '-'}
                                                    </Td>
                                                </Tr>
                                            ))
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardBody>
                </Card>

                {/* Modal d'ajout de contribution */}
                <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
                    <ModalOverlay />
                    <ModalContent maxH="90vh">
                        <ModalHeader>Ajouter une contribution</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <VStack spacing={4}>
                                <FormControl isRequired>
                                    <FormLabel>Membre</FormLabel>
                                    <MemberSelect
                                        members={members}
                                        value={formData.user_id}
                                        onChange={(id) => setFormData({ ...formData, user_id: id })}
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>Type de contribution</FormLabel>
                                    <Select
                                        placeholder="Sélectionner un type"
                                        value={formData.type_id}
                                        onChange={(e) => setFormData({ ...formData, type_id: e.target.value })}
                                    >
                                        {contributionTypes.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.nom} {type.montant_suggere ? `(${formatAmount(type.montant_suggere)})` : ''}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>Montant (EUR)</FormLabel>
                                    <Input
                                        type="number"
                                        value={formData.montant}
                                        onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                                        placeholder="Ex: 50"
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>Date de contribution</FormLabel>
                                    <Input
                                        type="date"
                                        value={formData.date_contribution}
                                        onChange={(e) => setFormData({ ...formData, date_contribution: e.target.value })}
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>Méthode de paiement</FormLabel>
                                    <Select
                                        value={formData.mode_paiement}
                                        onChange={(e) => setFormData({ ...formData, mode_paiement: e.target.value })}
                                    >
                                        <option value="especes">Espèces</option>
                                        <option value="cheque">Chèque</option>
                                        <option value="virement">Virement</option>
                                    </Select>
                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                        SumUp, carte et PayPal ne sont jamais saisis manuellement — ils sont
                                        créés automatiquement et vérifiés lors du paiement en ligne du membre.
                                    </Text>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Référence de paiement</FormLabel>
                                    <Input
                                        value={formData.reference_paiement}
                                        onChange={(e) => setFormData({ ...formData, reference_paiement: e.target.value })}
                                        placeholder="Ex: CHQ-123456 ou Transaction SumUp"
                                    />
                                </FormControl>

                                {estSecretaire ? (
                                    <Alert status="info" borderRadius="md">
                                        <AlertIcon />
                                        <Text fontSize="sm">
                                            Cette saisie sera « En attente » : le trésorier, le président ou le pasteur la validera.
                                        </Text>
                                    </Alert>
                                ) : (
                                    <FormControl isRequired>
                                        <FormLabel>Statut</FormLabel>
                                        <Select
                                            value={formData.statut}
                                            onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                                        >
                                            <option value="paye">Payé</option>
                                            <option value="en_attente">En attente</option>
                                        </Select>
                                    </FormControl>
                                )}
                            </VStack>
                        </ModalBody>

                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onClose}>
                                Annuler
                            </Button>
                            <Button
                                colorScheme="green"
                                onClick={handleSubmit}
                                isDisabled={!formData.user_id || !formData.type_id || !formData.montant}
                            >
                                Enregistrer
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Modal de rejet avec motif obligatoire */}
                <Modal isOpen={isRejectOpen} onClose={onRejectClose} size="md">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Rejeter la contribution</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            {rejectTarget && (
                                <VStack align="stretch" spacing={3}>
                                    <Text fontSize="sm" color="gray.600">
                                        {rejectTarget.membre_nom} {rejectTarget.membre_prenom} — {formatAmount(rejectTarget.montant)} ({rejectTarget.type_nom})
                                    </Text>
                                    <FormControl isRequired>
                                        <FormLabel>Motif du rejet</FormLabel>
                                        <Textarea
                                            value={rejectReason}
                                            onChange={(e) => setRejectReason(e.target.value)}
                                            placeholder="Ex : chèque sans provision, doublon, erreur de saisie..."
                                            rows={4}
                                        />
                                    </FormControl>
                                </VStack>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onRejectClose}>Annuler</Button>
                            <Button colorScheme="red" onClick={confirmReject} isDisabled={!rejectReason.trim()}>
                                Rejeter
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Modal de détails d'une cotisation */}
                <Modal isOpen={isDetailsOpen} onClose={onDetailsClose} size="md">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Détails de la contribution</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={6}>
                            {detailsContrib && (
                                <VStack align="stretch" spacing={3}>
                                    <HStack justify="space-between">
                                        <Text color="gray.500">Membre</Text>
                                        <Text fontWeight="medium">{detailsContrib.membre_nom} {detailsContrib.membre_prenom}</Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text color="gray.500">Type</Text>
                                        <Text fontWeight="medium">{detailsContrib.type_nom}</Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text color="gray.500">Montant</Text>
                                        <Text fontWeight="medium">{formatAmount(detailsContrib.montant)}</Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text color="gray.500">Date</Text>
                                        <Text fontWeight="medium">{formatDate(detailsContrib.date_contribution)}</Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text color="gray.500">Mode de paiement</Text>
                                        <Text fontWeight="medium">{LIBELLES_MODE_PAIEMENT[detailsContrib.mode_paiement] || detailsContrib.mode_paiement}</Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text color="gray.500">Statut</Text>
                                        <Badge colorScheme={getStatusColor(detailsContrib.statut)}>
                                            {getStatusLabel(detailsContrib.statut)}
                                        </Badge>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text color="gray.500">Référence</Text>
                                        <Text fontWeight="medium">{detailsContrib.reference_paiement || '-'}</Text>
                                    </HStack>
                                    <Box>
                                        <Text color="gray.500" mb={1}>Notes</Text>
                                        <Text fontWeight="medium" whiteSpace="pre-wrap">
                                            {detailsContrib.notes || '—'}
                                        </Text>
                                    </Box>
                                </VStack>
                            )}
                        </ModalBody>
                    </ModalContent>
                </Modal>
            </Box>
        );
    }

    // Vue Membre
    return (
        <Box>
            <Heading size="lg" mb={6}>Mes Contributions</Heading>

            <Tabs>
                <TabList>
                    <Tab>Vue d'ensemble</Tab>
                    <Tab>Détails par mois</Tab>
                    <Tab>Historique annuel</Tab>
                </TabList>

                <TabPanels>
                    {/* Onglet Vue d'ensemble */}
                    <TabPanel>
                        {summary && (
                            <>
                                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
                                    <Stat bg="white" p={4} borderRadius="md" shadow="sm">
                                        <StatLabel>Total contributions</StatLabel>
                                        <StatNumber>{summary.summary.total_contributions}</StatNumber>
                                        <StatHelpText>Depuis votre adhésion</StatHelpText>
                                    </Stat>
                                    <Stat bg="white" p={4} borderRadius="md" shadow="sm">
                                        <StatLabel>Montant payé</StatLabel>
                                        <StatNumber color="green.500">
                                            {formatAmount(summary.summary.montant_paye)}
                                        </StatNumber>
                                        <StatHelpText>Validées</StatHelpText>
                                    </Stat>
                                    <Stat bg="white" p={4} borderRadius="md" shadow="sm">
                                        <StatLabel>En attente</StatLabel>
                                        <StatNumber color="orange.500">
                                            {formatAmount(summary.summary.montant_en_attente)}
                                        </StatNumber>
                                        <StatHelpText>Non validées</StatHelpText>
                                    </Stat>
                                    <Stat bg="white" p={4} borderRadius="md" shadow="sm">
                                        <StatLabel>Dernière contribution</StatLabel>
                                        <StatNumber fontSize="lg">
                                            {formatDate(summary.summary.derniere_contribution)}
                                        </StatNumber>
                                    </Stat>
                                </SimpleGrid>

                                {summary.by_type.length > 0 && (
                                    <Card mb={6}>
                                        <CardHeader>
                                            <Heading size="md">Répartition par type</Heading>
                                        </CardHeader>
                                        <CardBody>
                                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                                {summary.by_type.map((type) => (
                                                    <Box key={type.type_nom} p={3} bg="gray.50" borderRadius="md">
                                                        <Text fontWeight="semibold">{type.type_nom}</Text>
                                                        <Text fontSize="2xl" color="blue.600" fontWeight="bold">
                                                            {formatAmount(type.montant_total)}
                                                        </Text>
                                                        <Text fontSize="sm" color="gray.600">
                                                            {type.nombre} contribution(s)
                                                        </Text>
                                                    </Box>
                                                ))}
                                            </SimpleGrid>
                                        </CardBody>
                                    </Card>
                                )}

                                {/* Section pour payer avec SumUp */}
                                <Card>
                                    <CardHeader bg="blue.50">
                                        <Heading size="md">Effectuer une nouvelle contribution</Heading>
                                    </CardHeader>
                                    <CardBody>
                                        <Alert status="info" mb={4}>
                                            <AlertIcon />
                                            Vous pouvez effectuer une contribution directement en ligne via SumUp
                                        </Alert>
                                        
                                        <VStack spacing={4} align="stretch">
                                            {contributionTypes.map((type) => (
                                                <Flex 
                                                    key={type.id} 
                                                    justify="space-between" 
                                                    align="center" 
                                                    p={4} 
                                                    bg="white" 
                                                    borderRadius="md" 
                                                    border="1px" 
                                                    borderColor="gray.200"
                                                >
                                                    <Box>
                                                        <Text fontWeight="bold" fontSize="lg">{type.nom}</Text>
                                                        {type.description && (
                                                            <Text fontSize="sm" color="gray.600">{type.description}</Text>
                                                        )}
                                                        {type.montant_suggere && (
                                                            <Text fontSize="xl" color="blue.600" fontWeight="bold" mt={2}>
                                                                {formatAmount(type.montant_suggere)}
                                                            </Text>
                                                        )}
                                                    </Box>
                                                    {type.montant_suggere && (
                                                        <SumUpPaymentButton
                                                            contributionId={null}
                                                            amount={type.montant_suggere}
                                                            contributionType={type.nom}
                                                            onSuccess={() => {
                                                                toast({
                                                                    title: 'Paiement réussi',
                                                                    description: 'Votre contribution a été enregistrée',
                                                                    status: 'success',
                                                                    duration: 5000,
                                                                });
                                                                fetchData();
                                                            }}
                                                            onError={(error) => {
                                                                toast({
                                                                    title: 'Erreur de paiement',
                                                                    description: error.message,
                                                                    status: 'error',
                                                                    duration: 5000,
                                                                });
                                                            }}
                                                        />
                                                    )}
                                                </Flex>
                                            ))}
                                        </VStack>
                                    </CardBody>
                                </Card>
                            </>
                        )}
                    </TabPanel>

                    {/* Onglet Détails par mois */}
                    <TabPanel>
                        <Card mb={4}>
                            <CardBody>
                                <HStack>
                                    <Text fontWeight="medium">Année:</Text>
                                    <Select 
                                        value={selectedYear} 
                                        onChange={(e) => setSelectedYear(e.target.value)} 
                                        w="150px"
                                    >
                                        {years.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </Select>
                                </HStack>
                            </CardBody>
                        </Card>

                        <VStack spacing={4} align="stretch">
                            {months.map((monthName, monthIndex) => {
                                const monthContribs = contributionsByMonth[monthIndex] || [];
                                if (monthContribs.length === 0) return null;

                                const monthTotal = monthContribs.reduce((sum, c) =>
                                    c.statut === 'paye' ? sum + parseFloat(c.montant) : sum, 0
                                );

                                return (
                                    <Card key={monthIndex}>
                                        <CardHeader bg="blue.50">
                                            <HStack justify="space-between">
                                                <Heading size="md">{monthName} {selectedYear}</Heading>
                                                <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                                                    {formatAmount(monthTotal)}
                                                </Badge>
                                            </HStack>
                                        </CardHeader>
                                        <CardBody p={0}>
                                            <TableContainer>
                                                <Table size="sm">
                                                    <Thead>
                                                        <Tr>
                                                            <Th>Date</Th>
                                                            <Th>Type</Th>
                                                            <Th isNumeric>Montant</Th>
                                                            <Th>Statut</Th>
                                                        </Tr>
                                                    </Thead>
                                                    <Tbody>
                                                        {monthContribs.map((contrib) => (
                                                            <Tr key={contrib.id}>
                                                                <Td>{formatDate(contrib.date_contribution)}</Td>
                                                                <Td>{contrib.type_nom}</Td>
                                                                <Td isNumeric fontWeight="semibold">
                                                                    {formatAmount(contrib.montant)}
                                                                </Td>
                                                                <Td>
                                                                    <Badge colorScheme={getStatusColor(contrib.statut)}>
                                                                        {getStatusLabel(contrib.statut)}
                                                                    </Badge>
                                                                </Td>
                                                            </Tr>
                                                        ))}
                                                    </Tbody>
                                                </Table>
                                            </TableContainer>
                                        </CardBody>
                                    </Card>
                                );
                            })}

                            {Object.keys(contributionsByMonth).length === 0 && (
                                <Alert status="info">
                                    <AlertIcon />
                                    Aucune contribution pour l'année {selectedYear}
                                </Alert>
                            )}
                        </VStack>
                    </TabPanel>

                    {/* Onglet Historique annuel */}
                    <TabPanel>
                        {summary && summary.by_year.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <Heading size="md">Historique annuel</Heading>
                                </CardHeader>
                                <CardBody>
                                    <TableContainer>
                                        <Table variant="simple">
                                            <Thead>
                                                <Tr>
                                                    <Th>Année</Th>
                                                    <Th isNumeric>Nombre</Th>
                                                    <Th isNumeric>Montant total</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {summary.by_year.map((yearData) => (
                                                    <Tr key={yearData.annee}>
                                                        <Td fontWeight="semibold">{yearData.annee}</Td>
                                                        <Td isNumeric>{yearData.nombre}</Td>
                                                        <Td isNumeric fontWeight="bold" color="blue.600">
                                                            {formatAmount(yearData.montant_total)}
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>
                                </CardBody>
                            </Card>
                        )}
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    );
}
